import React, { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { useDimensions } from '@/page-components/utils/useDimensions';
import { CUST_ANIM } from '../../../../theme/customizer-tokens';
import { isLightColor } from '../../../configurator-v3/lib/color';
import { useIframeReveal, useScrollbarGutter } from '../../lib/iframePreview';
import { useLogoIframe } from '../../lib/useLogoIframe';
import { DotField, type DotFieldTuning } from '../DotField';
import { PREVIEW_LOADER_SRC } from './PreviewPlaceholder';
import { Root, GridOverlay, Loader } from './DevicePreview.styles';

export interface DevicePreviewProps {
  /** Live configurator preview URL rendered inside the device screens. */
  previewUrl: string | undefined;
  /** Chosen logo, posted into the iframes so the preview reflects it. */
  logoUrl: string | null;
  /** Configurator theme tokens, applied to each `.device-screen` so the notch /
   *  chin pick up the casino's top/bottom-nav colours. */
  tokens?: Record<string, string>;
  /** Deploy phase — drives the screen overlay: `pending` dims/locks the preview,
   *  `deploying` blurs + glows it, `live` shows it clean. */
  phase?: 'pending' | 'deploying' | 'live';
  /** `dual` = MacBook + iPhone (default); `phone` = the iPhone frame alone,
   *  standalone (used by the mobile "your website is live" screen). */
  variant?: 'dual' | 'phone';
}

interface Dims {
  width: number;
  height: number;
}

/** Inline `--container-width` / `--container-height` (unitless) for the scale calc. */
const containerVars = (d: Dims): CSSProperties =>
  ({ '--container-width': d.width, '--container-height': d.height }) as CSSProperties;

/** Distinct seeds so the two screens' dot fields drift independently (never in
 *  lockstep), the same way stacked logo slots are seeded by their index. */
const DESKTOP_SEED = 3;
const PHONE_SEED = 8;

/** The deploy preview runs the dot field GAPPIER than the calm logo tiles: a near-
 *  zero floor + sharper falloff + tighter blooms, so on the large screens the
 *  energy reads as it flows (dark space between the blooms) rather than the whole
 *  lattice firing at once. Bright cores stay vivid (`alphaMax` lifted a touch). */
const DEPLOY_FIELD: DotFieldTuning = { alphaMax: 0.5, alphaFloor: 0.0, gamma: 0.8, bloomScale: 0.75 };

type Phase = NonNullable<DevicePreviewProps['phase']>;

/**
 * Mount + cross-fade lifecycle for the deploying dot-field overlay. It mounts and
 * fades in (0→1) when deploying begins, then cross-fades out as the site deblurs
 * once live, and finally unmounts — stopping its canvas RAF. `mounted` gates the
 * canvas; `visible` drives the opacity transition. Mirrors the logo reveal, where
 * the grid stays up through the dissolve, then drops.
 */
const useGridReveal = (phase: Phase): { mounted: boolean; visible: boolean } => {
  const [mounted, setMounted] = useState(phase === 'deploying');
  const [visible, setVisible] = useState(false);
  const unmountRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    window.clearTimeout(unmountRef.current);
    if (phase === 'deploying') {
      setMounted(true);
      // Flip to visible on the NEXT frame so opacity animates 0→1 (mounting
      // straight at opacity 1 would pop in with no transition).
      const raf = window.requestAnimationFrame(() => setVisible(true));
      return () => window.cancelAnimationFrame(raf);
    }
    // pending / live → cross-fade out, then drop the canvas once it has faded.
    setVisible(false);
    unmountRef.current = window.setTimeout(() => setMounted(false), CUST_ANIM.OVERLAY_MS);
    return undefined;
  }, [phase]);

  useEffect(() => () => window.clearTimeout(unmountRef.current), []);

  return { mounted, visible };
};

/**
 * MacBook + iPhone preview of the live casino. Each device is laid out at its
 * native pixel box and fit via `transform: scale()`, driven by `--container-*`
 * vars that `useDimensions` writes on resize (root + each wrapper). See styles.
 */
export const DevicePreview = ({
  previewUrl,
  logoUrl,
  tokens,
  phase = 'live',
  variant = 'dual',
}: DevicePreviewProps): React.ReactElement => {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const desktopRef = useRef<HTMLDivElement>(null);
  const phoneRef = useRef<HTMLDivElement>(null);

  const rootDims = useDimensions(rootRef) as Dims;
  const desktopDims = useDimensions(desktopRef) as Dims;
  const phoneDims = useDimensions(phoneRef) as Dims;

  useLogoIframe(logoUrl);

  const grid = useGridReveal(phase);

  // Each screen's iframe carries the cross-origin casino. Over-size it by the
  // scrollbar gutter so its (un-stylable) scrollbar falls outside the clipped
  // `.device-screen` and stays hidden, and fade it in only once it has loaded and
  // settled — so the site doesn't pop in / reflow inside the mockup.
  const desktopFrame = useIframeReveal(previewUrl);
  const mobileFrame = useIframeReveal(previewUrl);
  const gutter = useScrollbarGutter();
  const frameStyle = (revealed: boolean): CSSProperties => ({
    width: `calc(100% + ${gutter}px)`,
    opacity: revealed ? 1 : 0,
    transition: `opacity ${CUST_ANIM.OVERLAY_MS}ms ease`,
  });
  // Brand body fill behind the loading screen (from the casino's design tokens); the
  // loader ink flips to dark on a light fill so it reads on either.
  const placeholderBg = tokens?.['--bg-default'] ?? tokens?.['--body-bg-default'];
  const loaderOnLight = isLightColor(placeholderBg);

  const screenStyle = tokens as CSSProperties | undefined;
  const phaseClass = phase === 'live' ? '' : phase; // 'pending' | 'deploying'
  const variantClass = variant === 'phone' ? 'phone-only' : '';

  return (
    <Root
      ref={rootRef}
      className={`dual-device-preview ${variantClass} ${phaseClass}`.replace(/\s+/g, ' ').trim()}
      style={containerVars(rootDims)}
    >
      {variant === 'dual' ? (
        <div className="preview-wrapper" ref={desktopRef} style={containerVars(desktopDims)}>
          <div className="device-container desktop">
            <div className="device-frame" />
            <div className="device-screen" style={screenStyle}>
              <div className="device-notch" />
              {previewUrl ? (
                <iframe
                  className="device-display"
                  id="secondary-desktop"
                  src={previewUrl}
                  title={t('Desktop preview')}
                  onLoad={desktopFrame.onLoad}
                  style={frameStyle(desktopFrame.revealed)}
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                />
              ) : (
                <div className="device-display" />
              )}
              <div className="device-chin" />
              <Loader
                $shown={!desktopFrame.revealed}
                $onLight={loaderOnLight}
                src={PREVIEW_LOADER_SRC}
                alt=""
                aria-hidden
              />
              {grid.mounted ? (
                <GridOverlay $visible={grid.visible} aria-hidden="true">
                  <DotField seed={DESKTOP_SEED} tuning={DEPLOY_FIELD} />
                </GridOverlay>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      <div className="preview-wrapper" ref={phoneRef} style={containerVars(phoneDims)}>
        <div className="device-container smartphone">
          <div className="device-frame" />
          <div className="device-screen" style={screenStyle}>
            <div className="device-notch" />
            {previewUrl ? (
              <iframe
                className="device-display"
                id="secondary-mobile"
                src={previewUrl}
                title={t('Mobile preview')}
                onLoad={mobileFrame.onLoad}
                style={frameStyle(mobileFrame.revealed)}
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              />
            ) : (
              <div className="device-display" />
            )}
            <div className="device-chin" />
            <Loader
              $shown={!mobileFrame.revealed}
              $onLight={loaderOnLight}
              src={PREVIEW_LOADER_SRC}
              alt=""
              aria-hidden
            />
            {grid.mounted ? (
              <GridOverlay $visible={grid.visible} aria-hidden="true">
                <DotField seed={PHONE_SEED} tuning={DEPLOY_FIELD} />
              </GridOverlay>
            ) : null}
          </div>
        </div>
      </div>
    </Root>
  );
};

export default DevicePreview;